import { useCallback, useContext, useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import { toast } from "react-toastify";

import ReactGA from "react-ga4";

import SemesterComments from "./SemesterComments";
import DisplayEprStudentInfo from "./DisplayEprStudentInfo";
import DisplayResources from "./DisplayResources";
import DisplaySleRatings from "./DisplaySleRatings";
import DisplaySemester from "./DisplaySemester";
import GuardianEprDao from "../../dao/GuardianEprDao";
import T from "../translations/Translations";
import TabGroup from "./TabGroup";

import { formatDate } from "../../utils/DateFormatter";
import { indexOfEprForMostRecentPdf } from "../../utils/pickEprForMostRecentPdf";
import { GlobalContext } from "../contextProvider/ContextProvider";
import GuardianFluencyWpm from "./GuardianFluencyWpm";
import { ConsolidatedStandardsReport } from "./ConsolidatedStandardsReport";
import { parentFacingSubjectNames } from "../../utils/guardianVisibleSubjects";

/**
 * @description React component for displaying the most recent EPR
 * @name MostRecentEprs
 * @param {func} downloadPdf
 * @param {{}} eprs
 * @param {bool} loader
 * @returns {JSX.Element}
 */
const MostRecentEprs = ({
    downloadPdf,
    eprs,
    loader,
    nextStartDate,
    trackGA = true,
    reportableContent = null,
    diffRatings = []
}) => {
    const { state } = useContext(GlobalContext);
    const { token } = state || {};
    /** Loaded for this preview only, so a later open picks up resource adds and deletes. */
    const [eprResources, setEprResources] = useState(null);

    const [activeEpr, setActiveEpr] = useState(null);
    const [activeSubject, setActiveSubject] = useState(0);
    const [selectedEpr, setSelectedEpr] = useState(null);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [subjectAreas, setSubjectAreas] = useState(null);
    const [tabs, setTabs] = useState(null);

    const eprAvailable = `${T({ key: "EPR_Available" })} ${formatDate(nextStartDate)}`;
    const eprNotFound = T({ key: "EPR_Not_Found" });
    const subjectSelected = T({ key: "Subject_Selected" });

    /**
     * @description Handle the change of the active EPR
     * @callback
     * @name handleChangeEpr
     * @param {Event} e
     * @returns {void}
     */
    const handleChangeEpr = useCallback(
        (e) => {
            e.preventDefault();
            const index = parseInt(e.target.id.split("-")[1]);
            setActiveEpr(index);
            setSelectedEpr(eprs[index]);
        },
        [eprs]
    );

    /**
     * @description Handle the change of the active subject
     * @callback
     * @name handleChangeSubject
     * @param {Event} e
     * @returns {void}
     */
    const handleChangeSubject = useCallback(
        (e) => {
            e.preventDefault();
            const index = parseInt(e.target.id.split("-")[1]);
            setActiveSubject(index);
            setSelectedSubject(subjectAreas[index]);
        },
        [subjectAreas]
    );

    /**
     * send a pageview for selecting a student
     */
    useEffect(() => {
        if (!trackGA) return;
        const page = process.env.NODE_ENV !== "production" ? "/guardian/current-eprs -> dev" : "/guardian/current-eprs";
        ReactGA.send({
            hitType: "pageview",
            page,
            title: document.title
        });
    }, [trackGA]);

    /**
     * Set the available subjects
     */
    useEffect(() => {
        if (!selectedEpr) {
            return;
        }

        const getSubjectAreas = () => {
            const { gradingCriteriaDtos } = selectedEpr;
            const uniqueSubjects = parentFacingSubjectNames(gradingCriteriaDtos, {
                reportableContent,
                diffRatings
            });
            setSubjectAreas(uniqueSubjects);
            setSelectedSubject(uniqueSubjects[0] || null);
            setActiveSubject(0);
        };

        getSubjectAreas();
    }, [diffRatings, reportableContent, selectedEpr]);

    /**
     * Set the available semester tabs, initial epr, and grading criteria.
     * Do not treat an empty eprs array as initialized — teacher preview often
     * mounts before studentProfileData is ready; [] would set tabs=[] and
     * block reinits forever (empty array is truthy).
     */
    useEffect(() => {
        if ((tabs && tabs.length > 0) || !eprs?.length) {
            return;
        }

        const getEprLabels = () => {
            const eprLabels = eprs.map((epr) => {
                return epr.semester;
            });
            const initialIndex = indexOfEprForMostRecentPdf(eprs);
            setTabs(eprLabels);
            setActiveEpr(initialIndex);
            setSelectedEpr(eprs[initialIndex]);
        };

        getEprLabels();
    }, [eprs, tabs]);

    /**
     * Keep selectedEpr in sync when the same semester's epr object is replaced
     * (e.g. teacher preview enrich overlays customData.readingFluency).
     */
    useEffect(() => {
        if (!eprs?.length || activeEpr == null || !tabs?.length) return;
        const next = eprs[activeEpr];
        if (next && next !== selectedEpr) {
            setSelectedEpr(next);
        }
    }, [eprs, activeEpr, tabs, selectedEpr]);

    const resourceProfileId = selectedEpr?.gradingProfileId;
    const resourceStudentNumber = selectedEpr?.studentNumber;

    useEffect(() => {
        if (!resourceProfileId || resourceStudentNumber == null || resourceStudentNumber === "" || !token) {
            return undefined;
        }

        let cancelled = false;
        setEprResources(null);

        const fetchResources = async () => {
            try {
                const options = {
                    action: "eprStudentResourcesRead",
                    gradingProfileId: resourceProfileId,
                    params: {
                        studentNumbers: resourceStudentNumber
                    },
                    token
                };
                const response = await GuardianEprDao(options);
                if (cancelled) return;
                if (response && response.data) {
                    setEprResources(response.data.payload);
                }
            } catch (error) {
                if (cancelled) return;
                toast.error("Unable to retrieve student resources.");
                console.error(`Unable to retrieve resources:\n`, error);
            }
        };

        fetchResources();

        return () => {
            cancelled = true;
        };
    }, [resourceProfileId, resourceStudentNumber, token]);

    return (
        <div className="guardian-view-container">
            {selectedEpr && !nextStartDate && (
                <>
                    <DisplayEprStudentInfo
                        activeTab={activeEpr}
                        downloadPdf={downloadPdf}
                        epr={selectedEpr}
                        eprs={eprs}
                        handleChangeEpr={handleChangeEpr}
                        loader={loader}
                        tabs={tabs}
                    />
                    {eprResources?.length > 0 && <DisplayResources epr={selectedEpr} resources={eprResources} />}
                    <DisplaySemester epr={selectedEpr} />
                    <DisplaySleRatings
                        gradingProfileSleComment={selectedEpr.gradingProfileSleComment}
                        sleRatings={selectedEpr.sleRatings}
                        studentName={selectedEpr.studentName}
                        studentSleComment={selectedEpr.studentSleComment}
                    />
                    {subjectAreas?.length > 0 && (
                        <div className="guardian-grey-container">
                            <div className="guardian-tab-title">{subjectSelected}</div>
                            <TabGroup activeTab={activeSubject} handleClick={handleChangeSubject} tabs={subjectAreas} />
                            {selectedSubject && (
                                <>
                                    <SemesterComments selectedEpr={selectedEpr} selectedSubject={selectedSubject} />
                                    <ConsolidatedStandardsReport
                                        gradeName={selectedEpr.gradeName}
                                        leadingContent={
                                            <GuardianFluencyWpm
                                                selectedEpr={selectedEpr}
                                                selectedSubject={selectedSubject}
                                            />
                                        }
                                        rawData={selectedEpr.gradingCriteriaDtos}
                                        subjectName={selectedSubject}
                                        reportableContent={reportableContent}
                                        diffRatings={diffRatings}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </>
            )}
            {(!eprs || eprs?.length < 1 || nextStartDate) && (
                <div className="m-4">{nextStartDate ? eprAvailable : eprNotFound}</div>
            )}
        </div>
    );
};

MostRecentEprs.propTypes = {
    downloadPdf: PropTypes.func.isRequired,
    eprs: PropTypes.instanceOf(Array),
    loader: PropTypes.bool.isRequired,
    nextStartDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    trackGA: PropTypes.bool,
    reportableContent: PropTypes.instanceOf(Object),
    diffRatings: PropTypes.instanceOf(Array)
};

MostRecentEprs.defaultProps = {
    nextStartDate: null,
    eprs: [],
    trackGA: true,
    reportableContent: null,
    diffRatings: []
};

export default MostRecentEprs;
